import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, Shield, Eye, Sparkles, History } from 'lucide-react';
import { toast } from 'sonner';
import { getPermissionsCatalog, getUserPermissions, updateUserPermissions, getUserPermissionsAudit } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Human-readable labels for modules
const MODULE_LABELS = {
  dashboard: 'Dashboard',
  shipments: 'Embarques',
  containers: 'Contenedores',
  payments: 'Pagos / Balance',
  certificates: 'Certificados',
  tracking: 'Tracking',
  reports: 'Reportes',
  sla: 'SLA',
  clients: 'Clientes',
  importers: 'Importadores',
  suppliers: 'Proveedores',
  catalogs: 'Catálogos',
  users: 'Usuarios',
  audit: 'Auditoría',
  alerts: 'Alertas',
};

// Human-readable labels for fields
const FIELD_LABELS = {
  view_costs: 'Ver costos (compras, fletes internos)',
  view_prices: 'Ver precios de venta',
  view_amounts: 'Ver montos de pagos / facturas',
  view_profit: 'Ver utilidad / margen',
  view_suppliers: 'Ver proveedores',
  view_internal_notes: 'Ver notas internas',
};

export function PermissionsDialog({ open, onOpenChange, user, readOnly = false }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({ modules: [], fields: [], templates: [] });
  const [perms, setPerms] = useState({ modules: {}, fields: {} });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [activeTab, setActiveTab] = useState('editor');
  const [auditEntries, setAuditEntries] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    setSelectedTemplate('');
    setActiveTab('editor');
    Promise.all([getPermissionsCatalog(), getUserPermissions(user.id)])
      .then(([catRes, permRes]) => {
        setCatalog(catRes.data);
        setPerms(permRes.data);
      })
      .catch(() => toast.error('Error al cargar permisos'))
      .finally(() => setLoading(false));
  }, [open, user]);

  const loadAudit = () => {
    if (!user || loadingAudit) return;
    setLoadingAudit(true);
    getUserPermissionsAudit(user.id)
      .then((res) => setAuditEntries(res.data || []))
      .catch(() => toast.error('Error al cargar historial'))
      .finally(() => setLoadingAudit(false));
  };

  useEffect(() => {
    if (open && activeTab === 'history' && user) {
      loadAudit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, open]);

  const toggleModule = (key) => {
    if (readOnly) return;
    setPerms((prev) => ({
      ...prev,
      modules: { ...prev.modules, [key]: !prev.modules[key] },
    }));
  };

  const toggleField = (key) => {
    if (readOnly) return;
    setPerms((prev) => ({
      ...prev,
      fields: { ...prev.fields, [key]: !prev.fields[key] },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserPermissions(user.id, perms);
      toast.success('Permisos actualizados');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const allModulesOn = () => {
    if (readOnly) return;
    const next = Object.fromEntries(catalog.modules.map((m) => [m, true]));
    setPerms((prev) => ({ ...prev, modules: next }));
  };

  const allModulesOff = () => {
    if (readOnly) return;
    const next = Object.fromEntries(catalog.modules.map((m) => [m, false]));
    setPerms((prev) => ({ ...prev, modules: next }));
  };

  const applyTemplate = (code) => {
    if (readOnly || !code) return;
    const tpl = (catalog.templates || []).find((t) => t.code === code);
    if (!tpl) return;
    setPerms({
      modules: { ...tpl.permissions.modules },
      fields: { ...tpl.permissions.fields },
    });
    setSelectedTemplate(code);
    toast.success(`Plantilla "${tpl.name}" aplicada. Revisa y guarda.`);
  };

  if (!user) return null;

  const isAdminTarget =
    user.role === 'admin' || user.is_master_admin || user.is_super_admin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Permisos de {user.name}
          </DialogTitle>
          <DialogDescription>
            {isAdminTarget
              ? 'Los admins tienen acceso completo y no pueden ser restringidos.'
              : readOnly
              ? 'Vista de solo lectura. Solo los admins pueden editar permisos.'
              : 'Controla qué módulos y campos sensibles puede ver este usuario. Por defecto todo está visible; desactiva lo que no quieras que vea.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isAdminTarget ? (
          <div className="py-8 text-center text-muted-foreground" data-testid="admin-no-restrictions">
            Este usuario es administrador y tiene acceso a todo.
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="editor" data-testid="tab-permissions-editor">
                <Shield className="h-3.5 w-3.5 mr-1.5" /> Editor
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-permissions-history">
                <History className="h-3.5 w-3.5 mr-1.5" /> Historial
              </TabsTrigger>
            </TabsList>

            <TabsContent value="editor">
          <div className="space-y-6">
            {/* Templates selector */}
            {!readOnly && (catalog.templates || []).length > 0 && (
              <section className="rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="templates-section">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Aplicar plantilla</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Presets listos. Elige uno y ajusta lo que quieras antes de guardar.
                </p>
                <div className="flex items-center gap-2">
                  <Select value={selectedTemplate} onValueChange={applyTemplate}>
                    <SelectTrigger className="flex-1" data-testid="template-select">
                      <SelectValue placeholder="Elegir plantilla…" />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.templates.map((tpl) => (
                        <SelectItem key={tpl.code} value={tpl.code} data-testid={`template-option-${tpl.code}`}>
                          <div className="flex flex-col">
                            <span className="font-medium">{tpl.name}</span>
                            <span className="text-xs text-muted-foreground">{tpl.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedTemplate && (
                  <p className="text-xs text-primary mt-2">
                    Plantilla aplicada — ajusta los toggles abajo si hace falta y guarda.
                  </p>
                )}
              </section>
            )}

            {/* Modules section */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Módulos ({Object.values(perms.modules || {}).filter(Boolean).length}/{catalog.modules.length} activos)
                </h3>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" type="button" onClick={allModulesOn} data-testid="modules-all-on">
                      Todos
                    </Button>
                    <Button variant="outline" size="sm" type="button" onClick={allModulesOff} data-testid="modules-all-off">
                      Ninguno
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border border-border/60 rounded-md p-3">
                {catalog.modules.map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded hover:bg-accent/40 transition-colors"
                  >
                    <Label htmlFor={`mod-${m}`} className="flex-1 cursor-pointer text-sm">
                      {MODULE_LABELS[m] || m}
                    </Label>
                    <Switch
                      id={`mod-${m}`}
                      checked={!!perms.modules?.[m]}
                      onCheckedChange={() => toggleModule(m)}
                      disabled={readOnly}
                      data-testid={`perm-module-${m}`}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Fields section */}
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Campos sensibles
              </h3>
              <div className="grid grid-cols-1 gap-2 border border-border/60 rounded-md p-3">
                {catalog.fields.map((f) => (
                  <div
                    key={f}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded hover:bg-accent/40 transition-colors"
                  >
                    <Label htmlFor={`field-${f}`} className="flex-1 cursor-pointer text-sm">
                      {FIELD_LABELS[f] || f}
                    </Label>
                    <Switch
                      id={`field-${f}`}
                      checked={!!perms.fields?.[f]}
                      onCheckedChange={() => toggleField(f)}
                      disabled={readOnly}
                      data-testid={`perm-field-${f}`}
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
            </TabsContent>

            <TabsContent value="history">
              <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="permissions-audit-list">
                {loadingAudit ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : auditEntries.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    Aún no hay cambios registrados para este usuario.
                  </div>
                ) : (
                  auditEntries.map((entry) => {
                    const prevMods = entry.previous?.modules || {};
                    const newMods = entry.new?.modules || {};
                    const prevFields = entry.previous?.fields || {};
                    const newFields = entry.new?.fields || {};
                    const modChanges = Object.keys(newMods).filter((k) => prevMods[k] !== newMods[k]);
                    const fieldChanges = Object.keys(newFields).filter((k) => prevFields[k] !== newFields[k]);
                    const date = entry.created_at ? new Date(entry.created_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
                    return (
                      <div
                        key={entry.id}
                        className="border border-border/60 rounded-md p-3 text-sm bg-card/40"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{entry.actor_name || entry.actor_email}</span>
                          <span className="text-xs text-muted-foreground">{date}</span>
                        </div>
                        {modChanges.length === 0 && fieldChanges.length === 0 ? (
                          <div className="text-xs text-muted-foreground">Sin cambios efectivos.</div>
                        ) : (
                          <div className="space-y-1 text-xs">
                            {modChanges.map((k) => (
                              <div key={`m-${k}`} className="flex items-center gap-2">
                                <span className="text-muted-foreground">Módulo</span>
                                <span className="font-mono">{MODULE_LABELS[k] || k}</span>
                                <span className={prevMods[k] ? 'text-red-400' : 'text-green-500'}>
                                  {prevMods[k] ? '✓ → ✗' : '✗ → ✓'}
                                </span>
                              </div>
                            ))}
                            {fieldChanges.map((k) => (
                              <div key={`f-${k}`} className="flex items-center gap-2">
                                <span className="text-muted-foreground">Campo</span>
                                <span className="font-mono">{FIELD_LABELS[k] || k}</span>
                                <span className={prevFields[k] ? 'text-red-400' : 'text-green-500'}>
                                  {prevFields[k] ? '✓ → ✗' : '✗ → ✓'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="permissions-close-btn">
            Cerrar
          </Button>
          {!readOnly && !isAdminTarget && (
            <Button onClick={handleSave} disabled={saving || loading} data-testid="permissions-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar permisos
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
